import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import { TownMap } from "@/components/TownMap";
import api, { formatApiError } from "@/lib/api";
import { Loader2, MapPin, Plus, AlertOctagon, AlertTriangle, LayoutGrid } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { t } from "@/lib/strings";
import { cleanReports, localityCoords, ISRAEL_CENTER, localityName } from "@/lib/localityUtils";

const LOCAL_RADIUS_KM = 20;

function distanceKm(a, b) {
  if (!a || !b) return Infinity;
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export default function MapPage() {
  const { language } = useI18n();
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | emergency | issue
  const [center, setCenter] = useState(() => {
    const c = localityCoords(user?.city_id);
    return c ? [c.lat, c.lng] : null;
  });
  const [centerSource, setCenterSource] = useState(localityCoords(user?.city_id) ? "inline" : null);

  const cityLabel = localityName(user?.city_id, language) || (
    language === "ar" ? "موقعك" : language === "he" ? "המיקום שלך" : "your area"
  );

  // Resolve center from backend if we don't have inline coords
  useEffect(() => {
    if (center) return; // already resolved
    if (!user?.city_id) {
      setCenter([ISRAEL_CENTER.lat, ISRAEL_CENTER.lng]);
      setCenterSource("fallback");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/localities/${user.city_id}/coords`);
        if (cancelled) return;
        setCenter([data.lat, data.lng]);
        setCenterSource(data.source);
      } catch {
        if (cancelled) return;
        setCenter([ISRAEL_CENTER.lat, ISRAEL_CENTER.lng]);
        setCenterSource("fallback");
      }
    })();
    return () => { cancelled = true; };
  }, [user?.city_id]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reports");
        setReports(cleanReports(data).filter((r) => r.lat && r.lng));
      } catch (e) { console.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  // Only show reports inside the user's locality radius.
  const local = useMemo(() => {
    if (!center) return reports;
    const c = { lat: center[0], lng: center[1] };
    return reports.filter((r) => distanceKm(c, { lat: r.lat, lng: r.lng }) <= LOCAL_RADIUS_KM);
  }, [reports, center]);

  const filtered = useMemo(() => {
    if (filter === "emergency") return local.filter((r) => (r.category || "").toLowerCase() === "emergency");
    if (filter === "issue") return local.filter((r) => (r.category || "").toLowerCase() !== "emergency");
    return local;
  }, [local, filter]);

  const showCount = filtered.length > 0;

  return (
    <MobileShell
      padBottom={false}
      topBar={
        <>
          <div className="h-14 flex items-center justify-between px-4">
            <h1 className="font-extrabold text-[20px] text-[var(--te-text)] truncate" dir="auto">
              {t("map_title", language)}
            </h1>
            {showCount && (
              <span className="te-pill text-[11px] shrink-0" data-testid="map-count-pill">
                <MapPin size={11} /> {t("map_pins", language, filtered.length)}
              </span>
            )}
          </div>
          {/* Sticky filter row stays fixed; the map below it gets clipped, not the row */}
          <div className="px-4 py-2 flex items-center gap-2 overflow-x-auto te-no-scrollbar border-t border-[var(--te-divider)] bg-white">
            <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testId="map-filter-all">
              <LayoutGrid size={12} /> {t("reports_filter_all", language)}
            </FilterChip>
            <FilterChip active={filter === "emergency"} onClick={() => setFilter("emergency")} testId="map-filter-emergency">
              <AlertOctagon size={12} className="text-[var(--te-danger)]" /> Emergency
            </FilterChip>
            <FilterChip active={filter === "issue"} onClick={() => setFilter("issue")} testId="map-filter-issue">
              <AlertTriangle size={12} className="text-[var(--te-primary)]" /> Issues
            </FilterChip>
            <span className="ml-auto rtl:ml-0 rtl:mr-auto text-[11px] text-[var(--te-text-muted)] inline-flex items-center gap-1 shrink-0" dir="auto">
              <MapPin size={11} /> {cityLabel}
            </span>
          </div>
        </>
      }
    >
      {/* Map container — clamped to mobile viewport so it never goes under the
          bottom tabs or the PWA install banner. On desktop (phone frame),
          uses a fixed 560px. */}
      <div
        className="relative px-3 pt-3"
        style={{ minHeight: "300px" }}
        data-testid="map-container"
      >
        <div
          className="w-full"
          style={{
            // Viewport minus: top bar (56) + filter row (44) + bottom tab (64)
            // + install banner reserved space (70) + safe area + breathing room.
            height: "min(560px, calc(100dvh - 56px - 44px - 64px - 70px - env(safe-area-inset-bottom, 0px) - 24px))",
          }}
        >
          {loading || !center ? (
            <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin text-[var(--te-primary)]" /></div>
          ) : (
            <TownMap
              reports={filtered}
              center={center}
              zoom={filtered.length ? 12 : 11}
              fillHeight
            />
          )}
        </div>

        {/* Floating add button — positioned above tabs + install banner */}
        <Link
          to="/report/new"
          data-testid="map-fab-add"
          className="absolute right-6 w-14 h-14 rounded-full bg-[var(--te-primary)] text-white shadow-[0_10px_25px_-5px_rgba(103,58,183,0.6)] flex items-center justify-center hover:bg-[var(--te-primary-hover)] transition-transform active:scale-95"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 90px)" }}
          aria-label={t("reports_new_button", language)}
        >
          <Plus size={26} strokeWidth={2.4} />
        </Link>
      </div>
    </MobileShell>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${
        active
          ? "bg-[var(--te-primary)] text-white border-[var(--te-primary)]"
          : "bg-white text-[var(--te-text)] border-[var(--te-divider)] hover:bg-[var(--te-bg-soft)]"
      }`}
    >
      {children}
    </button>
  );
}
